import { GROUP_TOPIC_TYPES } from 'util/constants'

import { MEDIA_TYPES } from 'components/timelines/lightbox'
import { getApiWelcomeMessages } from 'reduxes/slices/api/selectors'
import { getSelectedTopicId } from 'reduxes/slices/selected/selectors'
import { createSelector } from 'reselect/lib/index'

import {
  getApiFiles,
  getApiParticipants,
  getApiTimelines,
  getApiTopics,
} from '../api/selectors'
import { getCurrentTeammateHandle } from '../current/selectors'
import { getLiveChatsPaging, getTopicsPaging } from '../paging/selectors'
import { getOpenParticipantsWithCurrentHandle } from '../participants/selectors'

const emptyArray = []

export const getSelectedTopic = createSelector(
  [getSelectedTopicId, getApiTopics],
  (selectedTopic, topics) => {
    return topics[selectedTopic]
  },
)

export const makeGetTopicById = () =>
  createSelector([getApiTopics, (_, id) => id], (topics, id) => {
    return topics?.[id]
  })

export const getFilesForSelectedTopic = createSelector(
  [getSelectedTopicId, getApiFiles],
  (selectedTopic, files) => {
    return files[selectedTopic]?.files || emptyArray
  },
)

export const getLightboxFilesForSelectedTopic = createSelector(
  [getSelectedTopicId, getApiFiles],
  (selectedTopic, files) => {
    const topicFiles = files[selectedTopic]?.files || emptyArray
    return topicFiles.filter((file) =>
      MEDIA_TYPES.some((fileType) =>
        (file?.content_type || '').startsWith(fileType),
      ),
    )
  },
)

export const getWelcomeMessages = createSelector(
  [getApiWelcomeMessages],
  (welcomeMessages) => {
    const x = Object.values(welcomeMessages).reduce((acc, welcomeMessage) => {
      // remove sample welcome messages, if they have been cloned
      if (!acc[welcomeMessage.welcome_video] || welcomeMessage.account_id) {
        acc[welcomeMessage.welcome_video || welcomeMessage.id] = welcomeMessage
      }
      return acc
    }, {})
    return Object.values(x).sort(
      (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
    )
  },
)

export const getReadyWelcomeMessages = createSelector(
  [getApiWelcomeMessages],
  (welcomeMessages) => {
    const x = Object.values(welcomeMessages).reduce((acc, welcomeMessage) => {
      // remove sample welcome messages, if they have been cloned
      if (!acc[welcomeMessage.welcome_video] || welcomeMessage.account_id) {
        acc[welcomeMessage.welcome_video || welcomeMessage.id] = welcomeMessage
      }
      return acc
    }, {})

    return Object.values(x).filter((welcomeMessage)=> {
      return welcomeMessage.state === 'ready'
    }).sort(
      (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
    )
  },
)

export const getMatchingTopics = createSelector(
  [getApiTopics, getTopicsPaging],
  (topics, { showBefore }) => {
    return Object.values(topics)
      .filter((topic) => {
        // ignore topics older than what has explicitly been fetched, but allow new topics
        return (
          topic.participant_mode === 'group_chat' &&
          topic.sort_order >= showBefore
        )
      })
      .sort((a, b) => b.sort_order - a.sort_order)
  },
)

export const getMatchingLiveChats = createSelector(
  [getApiTopics, getLiveChatsPaging],
  (topics, { showBefore }) => {
    return Object.values(topics)
      .filter((topic) => {
        // ignore topics older than what has explicitly been fetched, but allow new topics
        return (
          topic.participant_mode === 'live_chat' &&
          topic.sort_order >= showBefore
        )
      })
      .sort((a, b) => b.sort_order - a.sort_order)
  },
)

export const getAllTopics = createSelector([getApiTopics], (topics) => {
  return Object.values(topics)
    .filter((topic) => GROUP_TOPIC_TYPES.includes(topic.participant_mode))
    .sort((a, b) => b.sort_order - a.sort_order)
})

export const getUnreadTopics = createSelector(
  [getApiTopics, getCurrentTeammateHandle, getApiParticipants],
  (topics, currentTeammateHandle, participants) => {
    const unread = []
    const allParticipants = Object.values(participants)

    Object.values(topics).forEach((t) => {
      if (!GROUP_TOPIC_TYPES.includes(t.participant_mode)) {
        return
      }

      const currentParticipant = allParticipants.find(
        (p) =>
          p.handle === currentTeammateHandle &&
          p.parent_type === 'Topic' &&
          p.parent_id === t.id,
      )

      if (!currentParticipant || !currentParticipant.badge) {
        return
      }

      unread.push({
        id: t.id,
        subject: t.subject,
        badge: currentParticipant.badge,
        participants: t.participants.length,
      })
    })

    return unread
  },
)

export const LATEST_TOPICS_COUNT = 10

export const getLatestOpenTopics = createSelector(
  [getApiTopics, getOpenParticipantsWithCurrentHandle],
  (allTopics, participants) => {
    const topics = participants.reduce((acc, participant) => {
      const topic = allTopics[participant.parent_id]
      if (topic?.participant_mode === 'group_chat') {
        acc.push(topic)
      }
      return acc
    }, [])
    return topics
      .sort((a, b) => b.sort_order - a.sort_order)
      .slice(0, LATEST_TOPICS_COUNT)
  },
)

export const getLatestOpenLiveChats = createSelector(
  [getApiTopics, getOpenParticipantsWithCurrentHandle],
  (allTopics, participants) => {
    const topics = participants.reduce((acc, participant) => {
      const topic = allTopics[participant.parent_id]
      if (topic?.participant_mode === 'live_chat') {
        acc.push(topic)
      }
      return acc
    }, [])
    return topics
      .sort((a, b) => b.sort_order - a.sort_order)
      .slice(0, LATEST_TOPICS_COUNT)
  },
)

export const makeGetCurrentParticipantForTopic = () =>
  createSelector(
    [getCurrentTeammateHandle, getApiParticipants, (_, props) => props.topicId],
    (currentTeammateHandle, participants, topicId) => {
      return Object.values(participants).find(
        (p) =>
          p.handle === currentTeammateHandle &&
          p.parent_type === 'Topic' &&
          p.parent_id === topicId,
      )
    },
  )

export const getTimelineThreadedFrom = createSelector(
  [getSelectedTopicId, getApiTopics, getApiTimelines],
  (selectedTopic, topics, timelines) => {
    const topic = topics?.[selectedTopic]
    const timelineId = topic?.thread_timeline_id
    return timelines?.[timelineId]
  },
)

export const getGroupTopics = createSelector([getApiTopics], (topics) => {
  return Object.values(topics).filter((topic) => {
    return topic.participant_mode === 'group_chat'
  })
})

export const getGroupTopicsCount = createSelector(
  [getGroupTopics],
  (topics) => {
    return topics.length
  },
)

export const getLiveChatTopics = createSelector([getApiTopics], (topics) => {
  return Object.values(topics)
    .filter((topic) => topic.participant_mode === 'live_chat')
    .sort((a, b) => b.sort_order - a.sort_order)
})

export const getLiveChatsCount = createSelector(
  [getLiveChatTopics],
  (topics) => {
    return topics.length
  },
)
